import { useFormContext } from "react-hook-form";
import { Trans, useLingui } from "@lingui/react/macro";
import Select from "@medoma/components/Select/Select";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { useEmployees } from "@/api/Employee";
import {
  informationFieldConfigValueSchema,
  type IInformationFieldConfigValue,
} from "@/api/Patients";
import { employeeName } from "@medoma/models/shifts";
import { deducedError } from "@/Utils/ErrorUtils";

export type PrimaryResponsibleInputFields = {
  primaryResponsibleId: string | undefined;
};

const PrimaryResponsibleInput = ({
  primaryResponsibleFieldConfig,
}: {
  primaryResponsibleFieldConfig: IInformationFieldConfigValue;
}) => {
  const { t } = useLingui();
  const {
    register,
    formState: { errors },
  } = useFormContext<PrimaryResponsibleInputFields>();

  const {
    data: employees,
    isPending: isPendingEmployees,
    isError: isErrorEmployees,
    error: errorEmployees,
  } = useEmployees();

  if (isErrorEmployees) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta medarbetare.`} ${deducedError(errorEmployees)}`}
      />
    );
  }

  const isRequired =
    primaryResponsibleFieldConfig ===
    informationFieldConfigValueSchema.enum.required;

  if (isPendingEmployees) {
    return (
      <Select
        label={isRequired ? t`Patientansvarig` : t`Patientansvarig (valfri)`}
        name="primaryResponsibleId"
      >
        <option>{t`Hämtar medarbetare`}...</option>
      </Select>
    );
  }

  return (
    <Select
      label={isRequired ? t`Patientansvarig` : t`Patientansvarig (valfri)`}
      {...register("primaryResponsibleId", {
        required: {
          value: isRequired,
          message: t`Välj patientansvarig`,
        },
      })}
      errorMessage={errors.primaryResponsibleId?.message}
    >
      <option value="">
        <Trans>Ingen</Trans>
      </option>
      {employees.map((employee) => (
        <option value={employee.id} key={employee.id}>
          {employeeName(employee, { employeeNameFormat: "full" })}
        </option>
      ))}
    </Select>
  );
};

export default PrimaryResponsibleInput;
